import { useState, useEffect, useCallback, useRef } from 'react';
import {
  listarNotificacoes, marcarComoLida, marcarTodasComoLidas, assinarNotificacoes, ehTabelaAusente,
} from './notificacoesApi';

// ─────────────────────────────────────────────────────────────────────────────
// A caixa de entrada do operador logado — o que o sininho conta e a tela de
// Notificações lista.
//
// Vive no contexto, como as filas: a notificação chega com o operador em
// qualquer tela, e o número no sininho tem de acompanhar. O realtime entrega
// na hora; o ciclo de 2 minutos é a rede de segurança para quando o realtime
// cai (ou a tabela ainda não está na publicação).
//
// `aoChegar` é chamado só para o que chega PELO REALTIME — o que já estava lá
// no login não vira aviso de novo.
// ─────────────────────────────────────────────────────────────────────────────

const INTERVALO_MS = 2 * 60 * 1000;

export function useNotificacoes(codigo, { aoChegar } = {}) {
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  // 'ok' · 'sem-tabela' (o SQL ainda não rodou) · 'erro'
  const [situacao, setSituacao] = useState('ok');
  const aoChegarRef = useRef(aoChegar);
  aoChegarRef.current = aoChegar;

  const recarregar = useCallback(async () => {
    if (!codigo) { setItens([]); return; }
    setCarregando(true);
    try {
      setItens(await listarNotificacoes(codigo));
      setSituacao('ok');
    } catch (err) {
      setSituacao(ehTabelaAusente(err.message) ? 'sem-tabela' : 'erro');
    } finally {
      setCarregando(false);
    }
  }, [codigo]);

  useEffect(() => {
    recarregar();
    const id = setInterval(recarregar, INTERVALO_MS);
    return () => clearInterval(id);
  }, [recarregar]);

  useEffect(() => assinarNotificacoes(codigo, (nova) => {
    if (!nova?.id) return;
    setItens(lista => (lista.some(n => n.id === nova.id) ? lista : [nova, ...lista]));
    if (aoChegarRef.current) aoChegarRef.current(nova);
  }), [codigo]);

  const marcarLida = useCallback(async (id) => {
    // Otimista: o número do sininho cai no clique, não depois da rede.
    setItens(lista => lista.map(n => (n.id === id && !n.lida_em ? { ...n, lida_em: new Date().toISOString() } : n)));
    try { await marcarComoLida(id); } catch (e) { recarregar(); }
  }, [recarregar]);

  const marcarTodas = useCallback(async () => {
    const agora = new Date().toISOString();
    setItens(lista => lista.map(n => (n.lida_em ? n : { ...n, lida_em: agora })));
    try { await marcarTodasComoLidas(codigo); } catch (e) { recarregar(); }
  }, [codigo, recarregar]);

  const naoLidas = itens.filter(n => !n.lida_em).length;

  return { itens, naoLidas, carregando, situacao, recarregar, marcarLida, marcarTodas };
}
